import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { THEMES, DEFAULT_THEME, getStoredTheme, setStoredTheme } from './themeStorage';

const ThemeContext = createContext({
  theme: DEFAULT_THEME,
  setTheme: () => {},
  THEMES,
});

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = getStoredTheme();
      if (stored && Object.values(THEMES).includes(stored)) {
        return stored;
      }
    }
    return DEFAULT_THEME;
  });

  const applyThemeToDOM = useCallback((newTheme) => {
    if (typeof document !== 'undefined') {
      const validTheme = Object.values(THEMES).includes(newTheme) ? newTheme : DEFAULT_THEME;

      // 1. Set explicit HTML dataset & attribute
      document.documentElement.dataset.theme = validTheme;
      document.documentElement.setAttribute('data-theme', validTheme);

      // 2. Clear old theme class tokens and add active ones
      const classesToRemove = ['theme-dark', 'theme-light', 'theme-hybrid', 'dark', 'light', 'hybrid'];
      document.documentElement.classList.remove(...classesToRemove);
      document.documentElement.classList.add(`theme-${validTheme}`, validTheme);

      // 3. Sync to <body>
      if (document.body) {
        document.body.dataset.theme = validTheme;
        document.body.setAttribute('data-theme', validTheme);
        document.body.classList.remove(...classesToRemove);
        document.body.classList.add(`theme-${validTheme}`, validTheme);
      }

      // 4. Update browser navbar theme-color
      const metaThemeColor = document.querySelector('meta[name="theme-color"]');
      if (metaThemeColor) {
        metaThemeColor.setAttribute('content', validTheme === 'light' ? '#F4F0E7' : '#070C12');
      }
    }
  }, []);

  const setTheme = useCallback((newTheme) => {
    if (!Object.values(THEMES).includes(newTheme)) return;

    // A. Apply to DOM immediately (instantaneous 0ms visual change)
    applyThemeToDOM(newTheme);

    // B. Save to localStorage immediately
    setStoredTheme(newTheme);

    // C. Update React context state
    setThemeState(newTheme);

    // D. Notify any external/window listeners
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('pbz-theme-changed', { detail: { theme: newTheme } }));
    }
  }, [applyThemeToDOM]);

  // Synchronize on mount and on state changes
  useEffect(() => {
    applyThemeToDOM(theme);
  }, [theme, applyThemeToDOM]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, THEMES }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;
